/**
 * LayersTabContent.tsx
 * Contenu de l'onglet Calques avec arborescence, propriétés et palette de couleurs
 * MIGRATION: Affiche maintenant les propriétés React au lieu du service Legacy
 */
import React from 'react';
import { useAppStore } from '../../../services/StateService';
import type { ShapeData, ShapeType } from '../../../types/ShapeTypes';
import { useAppCoordinatorOptional } from '../../../hooks/useAppCoordinator';
import { type TemplateUnit, UnitConverter } from '../../../types';
import { LayerSearch } from '../common/LayerSearch';
import { ConfirmModal } from '../common/ConfirmModal';
import { Eye, EyeOff, FileText, Image, Square, Circle, File } from 'lucide-react';

// Import des composants de propriétés React (extraits de PropertiesPanel.tsx)
// On les importe directement pour éviter la duplication
const NumericInput: React.FC<{
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
}> = ({ label, value, onChange, min, max, step = 1, unit }) => (
  <div className="control-group">
    <label>{label}</label>
    <div className="input-with-unit">
      <input
        type="number"
        className="input"
        value={Math.round(value * 100) / 100}
        onChange={e => onChange(parseFloat(e.target.value) || 0)}
        min={min}
        max={max}
        step={step}
      />
      {unit && <span className="input-unit">{unit}</span>}
    </div>
  </div>
);

const ColorInput: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ label, value, onChange }) => (
  <div className="control-group">
    <label>{label}</label>
    <div className="color-controls">
      <input
        type="color"
        className="color-picker"
        value={value}
        onChange={e => onChange(e.target.value)}
      />
      <input
        type="text"
        className="input"
        value={value.toUpperCase()}
        onChange={e => onChange(e.target.value)}
        pattern="^#[0-9A-Fa-f]{6}$"
      />
    </div>
  </div>
);

/**
 * Propriétés du document (quand rien n'est sélectionné)
 */
const DocumentProperties: React.FC = () => {
  const templateWidth = useAppStore(state => state.canvas.templateWidth);
  const templateHeight = useAppStore(state => state.canvas.templateHeight);
  const backgroundColor = useAppStore(state => state.canvas.backgroundColor);
  const gridEnabled = useAppStore(state => state.canvas.gridEnabled);
  const gridSize = useAppStore(state => state.canvas.gridSize);
  const dpi = useAppStore(state => state.project.dpi);
  const setTemplateDimensions = useAppStore(state => state.setTemplateDimensions);
  const setBackgroundColor = useAppStore(state => state.setBackgroundColor);
  const setGridEnabled = useAppStore(state => state.setGridEnabled);
  const setGridSize = useAppStore(state => state.setGridSize);

  // État pour l'unité sélectionnée
  const [selectedUnit, setSelectedUnit] = React.useState<TemplateUnit>('px');

  // Convertir les dimensions en pixels depuis l'unité sélectionnée
  // OPTIMISÉ: Arrondi pour éviter les erreurs de précision
  const convertToPixels = React.useCallback(
    (value: number, unit: TemplateUnit): number => {
      if (unit === 'px') return Math.round(value);
      const converted = UnitConverter.convert(value, unit, 'px', dpi);
      // Arrondir à l'entier le plus proche pour les pixels
      return Math.round(converted);
    },
    [dpi]
  );

  // Convertir les pixels vers l'unité sélectionnée
  // OPTIMISÉ: Précision adaptée selon l'unité
  const convertFromPixels = React.useCallback(
    (value: number, unit: TemplateUnit): number => {
      if (unit === 'px') return Math.round(value);
      const converted = UnitConverter.convert(value, 'px', unit, dpi);
      // Arrondir selon l'unité : 2 décimales pour mm/cm/inch, entier pour px
      if (unit === 'mm' || unit === 'cm' || unit === 'inch') {
        return Math.round(converted * 100) / 100; // 2 décimales
      }
      return Math.round(converted);
    },
    [dpi]
  );

  // Mettre à jour le CanvasManager quand les dimensions changent
  const appCoordinator = useAppCoordinatorOptional();
  const handleDimensionChange = React.useCallback(
    (width: number, height: number, unit: TemplateUnit = selectedUnit) => {
      // Convertir en pixels
      const widthPx = convertToPixels(width, unit);
      const heightPx = convertToPixels(height, unit);
      setTemplateDimensions(widthPx, heightPx);
      // Les dimensions sont gérées par StateService, pas besoin de CanvasManager
    },
    [appCoordinator, setTemplateDimensions, selectedUnit, convertToPixels]
  );

  // Gérer le changement d'unité
  const handleUnitChange = React.useCallback((newUnit: TemplateUnit) => {
    setSelectedUnit(newUnit);
  }, []);

  const handleBackgroundColorChange = React.useCallback(
    (color: string) => {
      setBackgroundColor(color);
      // La couleur de fond est gérée par StateService, pas besoin de CanvasManager
    },
    [appCoordinator, setBackgroundColor]
  );

  const handleGridToggle = React.useCallback(
    (enabled: boolean) => {
      setGridEnabled(enabled);
      // La grille est gérée par StateService, pas besoin de CanvasManager
    },
    [appCoordinator, setGridEnabled]
  );

  const handleGridSizeChange = React.useCallback(
    (size: number) => {
      setGridSize(size);
      // La taille de la grille est gérée par StateService, pas besoin de CanvasManager
    },
    [appCoordinator, setGridSize]
  );

  // Valeurs converties dans l'unité sélectionnée
  const widthInUnit = React.useMemo(
    () => convertFromPixels(templateWidth, selectedUnit),
    [templateWidth, selectedUnit, convertFromPixels]
  );
  const heightInUnit = React.useMemo(
    () => convertFromPixels(templateHeight, selectedUnit),
    [templateHeight, selectedUnit, convertFromPixels]
  );

  return (
    <>
      <div className="sidebar-section">
        <h3 className="section-title">Document</h3>
        <div className="properties-content">
          <div className="control-group">
            <label>Unité</label>
            <select
              className="input"
              value={selectedUnit}
              onChange={e => handleUnitChange(e.target.value as TemplateUnit)}
              style={{ width: '100%' }}
            >
              <option value="px">Pixels (px)</option>
              <option value="mm">Millimètres (mm)</option>
              <option value="cm">Centimètres (cm)</option>
              <option value="inch">Pouces (in)</option>
            </select>
          </div>
          <div className="control-group">
            <label>Largeur</label>
            <div className="input-with-unit">
              <input
                type="number"
                className="input"
                value={Math.round(widthInUnit * 100) / 100}
                onChange={e =>
                  handleDimensionChange(parseFloat(e.target.value) || 0, heightInUnit, selectedUnit)
                }
                min={selectedUnit === 'px' ? 100 : 0.1}
                max={selectedUnit === 'px' ? 5000 : 1000}
                step={selectedUnit === 'px' ? 1 : 0.1}
              />
              <span className="input-unit">{selectedUnit === 'inch' ? 'in' : selectedUnit}</span>
            </div>
          </div>
          <div className="control-group">
            <label>Hauteur</label>
            <div className="input-with-unit">
              <input
                type="number"
                className="input"
                value={Math.round(heightInUnit * 100) / 100}
                onChange={e =>
                  handleDimensionChange(widthInUnit, parseFloat(e.target.value) || 0, selectedUnit)
                }
                min={selectedUnit === 'px' ? 100 : 0.1}
                max={selectedUnit === 'px' ? 5000 : 1000}
                step={selectedUnit === 'px' ? 1 : 0.1}
              />
              <span className="input-unit">{selectedUnit === 'inch' ? 'in' : selectedUnit}</span>
            </div>
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-secondary)',
              marginTop: 'var(--spacing-xs)',
            }}
          >
            {templateWidth} × {templateHeight} px
          </div>
          <ColorInput
            label="Couleur de fond"
            value={backgroundColor}
            onChange={handleBackgroundColorChange}
          />
        </div>
      </div>

      <div className="sidebar-section">
        <h3 className="section-title">Grille et Guides</h3>
        <div className="properties-content">
          <div className="control-group">
            <label>
              <input
                type="checkbox"
                checked={gridEnabled}
                onChange={e => handleGridToggle(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Afficher la grille magnétique
            </label>
          </div>
          {gridEnabled && (
            <NumericInput
              label="Taille de la grille"
              value={gridSize}
              onChange={handleGridSizeChange}
              min={5}
              max={100}
              step={5}
              unit="px"
            />
          )}
        </div>
      </div>
    </>
  );
};

interface LayersTabContentProps {
  selectedShape: ShapeData | undefined;
  onShowVariablesModal?: () => void;
  onShowTextStylesPanel?: () => void;
}

/**
 * Composant LayersTabContent
 * Affiche maintenant les propriétés React au lieu du service Legacy
 */
export function LayersTabContent({
  selectedShape,
  onShowVariablesModal: _onShowVariablesModal,
  onShowTextStylesPanel,
}: LayersTabContentProps): React.ReactElement {
  const _appCoordinator = useAppCoordinatorOptional();
  const shapes = useAppStore(state => state.shapes);
  const updateShape = useAppStore(state => state.updateShape);
  const deleteShape = useAppStore(state => state.deleteShape);
  const duplicateShape = useAppStore(state => state.duplicateShape);

  // UX: État pour la recherche et les filtres de calques
  const [layerSearchQuery, setLayerSearchQuery] = React.useState('');
  const [layerFilters, setLayerFilters] = React.useState<Set<ShapeType>>(new Set());
  // A18 : calque en attente de suppression (ouvre la ConfirmModal).
  const [deleteTarget, setDeleteTarget] = React.useState<{ id: string; label: string } | null>(
    null
  );

  const confirmDeleteLayer = React.useCallback(() => {
    if (!deleteTarget) return;
    deleteShape(deleteTarget.id);
    // P3 : commit() ajouté par historyMiddleware, pas dans AppActions
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (useAppStore.getState() as any).commit?.();
    setDeleteTarget(null);
  }, [deleteTarget, deleteShape]);

  // UX: Filtrer les calques selon la recherche et les filtres
  const filteredShapes = React.useMemo(() => {
    let filtered = shapes;

    // Filtre par type
    if (layerFilters.size > 0) {
      filtered = filtered.filter(shape => layerFilters.has(shape.type));
    }

    // Recherche par nom
    if (layerSearchQuery.trim()) {
      const query = layerSearchQuery.toLowerCase();
      filtered = filtered.filter(shape => {
        const name = (shape.name || shape.type || '').toLowerCase();
        return name.includes(query);
      });
    }

    return filtered;
  }, [shapes, layerSearchQuery, layerFilters]);

  // Extraire les couleurs utilisées dans le document
  const documentColors = React.useMemo(() => {
    const colors = new Set<string>();
    shapes.forEach(shape => {
      if (shape.fill && typeof shape.fill === 'string') {
        colors.add(shape.fill);
      }
      if (shape.stroke && typeof shape.stroke === 'string') {
        colors.add(shape.stroke);
      }
    });
    return Array.from(colors).slice(0, 12); // Limiter à 12 couleurs
  }, [shapes]);

  // Handler pour mettre à jour une forme (avec debounce pour le texte)
  const debounceRef = React.useRef<NodeJS.Timeout | null>(null);
  const _handleUpdate = React.useCallback(
    (updates: Partial<ShapeData>) => {
      if (!selectedShape) return;
      // Pour le texte, utiliser un debounce pour éviter trop d'updates
      if ('text' in updates) {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
          updateShape(selectedShape.id, updates);
        }, 300);
      } else {
        updateShape(selectedShape.id, updates);
      }
    },
    [selectedShape, updateShape]
  );

  // Cleanup du debounce
  React.useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // UX: Composant LayerItem réutilisable (style Figma)
  const LayerItem: React.FC<{ shape: ShapeData }> = ({ shape }) => {
    // P0.2 FIX: Utiliser selection.primaryId au lieu de canvas.selectedNodeId
    const selectedNodeId = useAppStore(state => state.selection.primaryId);
    const isSelected = selectedNodeId === shape.id;
    const [editingName, setEditingName] = React.useState(false);
    const [nameValue, setNameValue] = React.useState(shape.name || shape.type);
    const nameInputRef = React.useRef<HTMLInputElement>(null);

    const commitRename = () => {
      const trimmed = nameValue.trim();
      if (trimmed && trimmed !== (shape.name || shape.type)) {
        updateShape(shape.id, { name: trimmed });
      } else {
        setNameValue(shape.name || shape.type);
      }
      setEditingName(false);
    };
    // PR-17 / P3 : Actions lock/hide câblées dans le store mais pas typées dans AppActions.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const setShapeLocked = useAppStore(state => (state as any).setShapeLocked) as (
      id: string,
      locked: boolean
    ) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const setShapeHidden = useAppStore(state => (state as any).setShapeHidden) as (
      id: string,
      hidden: boolean
    ) => void;

    const getTypeIcon = (type: ShapeType) => {
      switch (type) {
        case 'text':
          return (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="4 7 4 4 20 4 20 7" />
              <line x1="9" y1="20" x2="15" y2="20" />
              <line x1="12" y1="4" x2="12" y2="20" />
            </svg>
          );
        case 'image':
        case 'photoSpace':
          return (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          );
        case 'rect':
          return (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            </svg>
          );
        case 'circle':
          return (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
            </svg>
          );
        default:
          return (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            </svg>
          );
      }
    };

    return (
      <div
        className={`layer-item ${isSelected ? 'selected' : ''}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-sm)',
          padding: 'var(--spacing-sm) var(--spacing-md)',
          cursor: 'pointer',
          borderRadius: 'var(--radius-sm)',
          transition: 'background-color 0.2s',
          backgroundColor: isSelected
            ? 'var(--color-primary-light, rgba(59, 130, 246, 0.1))'
            : 'transparent',
        }}
        onMouseEnter={e => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
          }
        }}
        onMouseLeave={e => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
        onClick={() => {
          const store = useAppStore.getState();
          store.setSelectedNode(shape.id);
        }}
      >
        {/* UX: Miniature pour les images */}
        {shape.type === 'image' && shape.src ? (
          <div className="layer-item-thumbnail">
            <img src={shape.src} alt={shape.name || 'Image'} />
          </div>
        ) : (
          <div
            className="layer-icon"
            style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}
          >
            {getTypeIcon(shape.type)}
          </div>
        )}

        {/* Nom du calque — double-clic pour renommer (F2 / feature layer-rename). */}
        {editingName ? (
          <input
            ref={nameInputRef}
            value={nameValue}
            onChange={e => setNameValue(e.target.value)}
            onBlur={commitRename}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitRename();
              }
              if (e.key === 'Escape') {
                setNameValue(shape.name || shape.type);
                setEditingName(false);
              }
              e.stopPropagation();
            }}
            onClick={e => e.stopPropagation()}
            autoFocus
            style={{
              flex: 1,
              fontSize: 'var(--font-size-sm)',
              fontWeight: isSelected ? 600 : 400,
              background: 'var(--color-bg-primary, #1e1e2e)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-primary, #7C3AED)',
              borderRadius: 4,
              padding: '0 4px',
              outline: 'none',
              width: 0,
              minWidth: 60,
            }}
          />
        ) : (
          <span
            className="layer-name"
            style={{
              flex: 1,
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-primary)',
              fontWeight: isSelected ? 600 : 400,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            onDoubleClick={e => {
              e.stopPropagation();
              setNameValue(shape.name || shape.type);
              setEditingName(true);
            }}
            title="Double-cliquer pour renommer"
          >
            {shape.name || shape.type}
          </span>
        )}

        {/* PR-17: Boutons Eye / Lock */}
        <button
          className="p-1 rounded hover:bg-white/10"
          onClick={e => {
            e.stopPropagation();
            setShapeHidden(shape.id, !shape.hidden);
          }}
          title={shape.hidden ? 'Show' : 'Hide'}
          style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}
        >
          {shape.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>

        <button
          className="p-1 rounded hover:bg-white/10"
          onClick={e => {
            e.stopPropagation();
            setShapeLocked(shape.id, !shape.locked);
          }}
          title={shape.locked ? 'Unlock' : 'Lock'}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          {shape.locked ? '🔒' : '🔓'}
        </button>

        {/* UX: Badge de type */}
        {!shape.name && <span className="layer-item-badge">{shape.type}</span>}

        {/* UX: Bouton SUPPR visible */}
        <button
          className="layer-delete"
          title="Supprimer (Suppr)"
          onClick={e => {
            e.stopPropagation();
            setDeleteTarget({ id: shape.id, label: shape.name || shape.type });
          }}
          style={{
            padding: '4px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: 'var(--color-danger)',
            opacity: 0.7,
            transition: 'opacity 0.2s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.opacity = '1';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.opacity = '0.7';
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
        </button>
      </div>
    );
  };

  return (
    <>
      {/* CORRIGÉ: Panneau droit = UNIQUEMENT gestion des calques */}
      {/* Les propriétés d'objet sont maintenant dans le panneau gauche (Drawers) */}

      {/* Info sélection - résumé compact au lieu des propriétés détaillées */}
      {selectedShape && (
        <div
          className="selection-summary"
          style={{
            padding: 'var(--spacing-md)',
            marginBottom: 'var(--spacing-md)',
            background: 'var(--color-primary-light, rgba(59, 130, 246, 0.1))',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-primary, #3b82f6)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-sm)',
              marginBottom: 'var(--spacing-sm)',
            }}
          >
            <span style={{ fontSize: '16px', display: 'flex', alignItems: 'center' }}>
              {selectedShape.type === 'text' ? (
                <FileText className="w-4 h-4" />
              ) : selectedShape.type === 'image' || selectedShape.type === 'photoSpace' ? (
                <Image className="w-4 h-4" />
              ) : selectedShape.type === 'rect' ? (
                <Square className="w-4 h-4" />
              ) : selectedShape.type === 'circle' ? (
                <Circle className="w-4 h-4" />
              ) : (
                <File className="w-4 h-4" />
              )}
            </span>
            <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
              {selectedShape.name || `${selectedShape.type} sélectionné`}
            </span>
          </div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
            {Math.round(selectedShape.x)}×{Math.round(selectedShape.y)} ·{' '}
            {Math.round(selectedShape.width || 0)}×{Math.round(selectedShape.height || 0)} px
          </div>
          {/* Actions rapides */}
          <div
            className="object-actions"
            style={{ marginTop: 'var(--spacing-sm)', display: 'flex', gap: 'var(--spacing-xs)' }}
          >
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => duplicateShape(selectedShape.id)}
              style={{ flex: 1, padding: '6px 8px', fontSize: 'var(--font-size-xs)' }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ marginRight: '4px' }}
              >
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
              Dupliquer
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={() =>
                setDeleteTarget({
                  id: selectedShape.id,
                  label: selectedShape.name || selectedShape.type,
                })
              }
              style={{ flex: 1, padding: '6px 8px', fontSize: 'var(--font-size-xs)' }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ marginRight: '4px' }}
              >
                <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              Supprimer
            </button>
          </div>
          {/* Bouton Styles de texte - visible uniquement pour les textes */}
          {selectedShape.type === 'text' && onShowTextStylesPanel && (
            <div style={{ marginTop: 'var(--spacing-sm)' }}>
              <button
                onClick={() => {
                  onShowTextStylesPanel();
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: 'var(--color-bg-secondary)',
                  color: 'var(--color-text-primary)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
                title="Gérer les styles de texte"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M4 7h16M4 12h16M4 17h16" />
                </svg>
                Styles de texte
              </button>
            </div>
          )}
          <p
            style={{
              marginTop: 'var(--spacing-sm)',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-tertiary)',
              fontStyle: 'italic',
            }}
          >
            💡 Ouvrez l'onglet Texte/Éléments pour modifier les propriétés
          </p>
        </div>
      )}

      {/* Propriétés du document (seulement si rien n'est sélectionné) */}
      {!selectedShape && (
        <div style={{ marginBottom: 'var(--spacing-xl)' }}>
          <DocumentProperties />
        </div>
      )}

      {/* Panneau Calques */}
      <div className="layers-tree">
        {/* UX: Barre de recherche et filtres (style Figma) */}
        <LayerSearch
          searchQuery={layerSearchQuery}
          activeFilters={layerFilters}
          onSearchChange={setLayerSearchQuery}
          onFilterChange={setLayerFilters}
        />

        {/* FIX : la liste n'affichait QUE les images/photoSpace → textes et formes
            n'apparaissaient jamais. On affiche TOUTES les shapes (style Figma :
            le calque le plus en avant en premier). */}
        <div className="layer-folder">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="layer-icon"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <line x1="9" y1="3" x2="9" y2="21" />
          </svg>
          <span className="layer-name">
            {filteredShapes.length} {filteredShapes.length === 1 ? 'calque' : 'calques'}
          </span>
        </div>

        {filteredShapes.length === 0 ? (
          <div
            style={{
              padding: 'var(--spacing-md)',
              textAlign: 'center',
              color: 'var(--color-text-tertiary)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            Aucun élément. Ajoutez du texte ou des formes depuis la barre d'outils.
          </div>
        ) : (
          filteredShapes
            .slice()
            .reverse()
            .map(shape => <LayerItem key={shape.id} shape={shape} />)
        )}
      </div>

      {/* Panneau Palette de Couleurs */}
      <div
        style={{
          marginTop: 'var(--spacing-xl)',
          paddingTop: 'var(--spacing-xl)',
          borderTop: '1px solid var(--color-border)',
        }}
      >
        <h4
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--color-text-primary)',
            marginBottom: 'var(--spacing-md)',
          }}
        >
          Couleurs du document
        </h4>
        <div className="color-palette">
          {documentColors.length > 0 ? (
            documentColors.map((color, index) => (
              <div
                key={index}
                className="color-swatch"
                style={{ backgroundColor: color }}
                title={color}
              />
            ))
          ) : (
            <div
              style={{
                gridColumn: '1 / -1',
                textAlign: 'center',
                color: 'var(--color-text-tertiary)',
                fontSize: 'var(--font-size-sm)',
                padding: 'var(--spacing-md)',
              }}
            >
              Aucune couleur utilisée
            </div>
          )}
        </div>
      </div>

      {/* A18 : confirmation de suppression de calque */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        danger
        title="Supprimer ce calque ?"
        message={
          <>
            «&nbsp;<strong>{deleteTarget?.label}</strong>&nbsp;» sera supprimé du canvas. Vous
            pouvez annuler avec Ctrl+Z.
          </>
        }
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        onConfirm={confirmDeleteLayer}
        onClose={() => setDeleteTarget(null)}
      />
    </>
  );
}

export default LayersTabContent;
